import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

import { ANIMAL_TYPES } from '@/constants';
import { cosineDistanceToSimilarityPercent } from '@/lib/similarityFromCosine';
import { NbButton, NbCard, NbChip } from '@/components/Neobrut';

type Props = {
  pet: Pet;
  cosineDistance: number;
  matchedImageUrl: string | null | undefined;
};

export const SimilarMatchCard: FC<Props> = ({ pet, cosineDistance, matchedImageUrl }) => {
  const navigate = useNavigate();
  const img = matchedImageUrl || pet.gallery?.[0];
  const similarity = cosineDistanceToSimilarityPercent(cosineDistance);

  const dateLabel = useMemo(() => {
    if (pet.status === 'found' && pet.date_found) {
      return `Encontrado ${format(new Date(pet.date_found), 'dd/MM/yy', { locale: es })}`;
    }
    if (pet.status === 'lost' && pet.date_lost) {
      return `Perdido ${format(new Date(pet.date_lost), 'dd/MM/yy', { locale: es })}`;
    }
    return null;
  }, [pet.status, pet.date_found, pet.date_lost]);

  const addressShort = useMemo(() => {
    const a = pet.location?.address;
    if (!a) return null;
    const line = [a.road, a.house_number, a.city].filter(Boolean).join(' ');
    return line.length > 48 ? `${line.slice(0, 40)}…` : line;
  }, [pet.location]);

  return (
    <NbCard className="p-0">
      <div
        className="px-3 py-1.5 text-center text-[11px] font-bold uppercase tracking-[0.04em]"
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          borderBottom: '3px solid var(--nb-ink)',
          background: 'var(--nb-ink)',
          color: 'var(--nb-paper)',
        }}
      >
        SIMILITUD · {similarity.toFixed(1)}%
      </div>
      <div
        className="aspect-square w-full overflow-hidden"
        style={{ borderBottom: '3px solid var(--nb-ink)', background: '#e8e3d5' }}
      >
        {img ? (
          <img src={img} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="nb-hatch flex h-full items-center justify-center text-[10px] opacity-60">
            Sin imagen
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display min-w-0 truncate text-[20px] leading-none">
            {pet.name?.trim() || 'Sin nombre'}
          </h3>
          <NbChip fill={pet.status === 'lost' ? 'lost' : 'found'}>
            {pet.status === 'lost' ? 'Perdido' : 'Encontrado'}
          </NbChip>
        </div>
        <p className="text-[13px] font-medium">
          {ANIMAL_TYPES[pet.type]}
          {pet.race && ` · ${pet.race}`}
        </p>
        {dateLabel && (
          <p
            className="text-[10px] font-bold uppercase tracking-[0.04em] opacity-70"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            {dateLabel}
          </p>
        )}
        {addressShort && (
          <p className="text-[11px] font-semibold opacity-80">{addressShort}</p>
        )}
        <NbButton fill="lost" onClick={() => navigate(`/pet/${pet.id}`)}>
          Ver publicación
        </NbButton>
      </div>
    </NbCard>
  );
};
